/** Text Asset node — "文字 / 文本素材" Source/Asset card with Canvas-native
 * inline plain-text editing (VISTOERA_CANVAS_TEXT_ASSET_INLINE_EDITOR_CANONICAL_CARD_P0_V1,
 * VISTOERA_TEXT_ASSET_CANONICAL_HEADER_BODY_AND_MINIMAL_EXPANDED_EDITOR_UI_P0_V1,
 * VISTOERA_CANVAS_GLOBAL_REMOVE_COLLAPSE_ACTION_AND_TEXT_ASSET_TYPOGRAPHY_BOTTOM_HISTORY_ACTIONS_P0_V1,
 * VISTOERA_CANVAS_TEXT_ASSET_NODE_WIDER_EXPANDED_RESIZABLE_HEIGHT_AND_COPY_TEXT_ACTION_P1_V1,
 * VISTOERA_TEXT_ASSET_CANONICAL_WIDTH_AND_HOVER_NEXT_STEP_QUICK_ACTIONS_P0_V1).
 *
 * Compact by default; single click on the body expands in-place and focuses
 * a borderless textarea. Autosave via debounced `onUpdateTextAsset`; undo/redo
 * and copy-text live in the expanded body footer only. No Modal / Save / Cancel. */

import { CloseOutlined, CopyOutlined, RedoOutlined, SnippetsOutlined, UndoOutlined } from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Tooltip, message } from 'antd'
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { stopCanvasEditableKeyPropagation, useCanvasImeSafeTextHandlers } from '../canvasEditableKeyboardGuard'
import { canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { copyGenerateTextResultToClipboard } from '../generateTextCopy'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import {
  TEXT_ASSET_CARD_WIDTH_PX,
  TEXT_ASSET_CONTENT_VIEWPORT_HEIGHT_FIELD,
  clampTextAssetContentViewportHeight,
  resolveTextAssetContentViewportHeight,
} from '../textAssetPresentation'
import { useTextEditHistory } from '../useTextEditHistory'
import type { FlowNodeData } from '../types'
import CanvasResultQuickActionRail from './CanvasResultQuickActionRail'

const AUTOSAVE_DEBOUNCE_MS = 800
const COPY_FEEDBACK_MS = 1800

function TextAssetNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { nodeInstanceLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData
  const def = useNodeDefinition(nodeData.nodeType)
  const fields = nodeData.fields || {}
  const persistedText = (fields.text as string | undefined) ?? ''
  const persistedViewportHeight = resolveTextAssetContentViewportHeight(fields[TEXT_ASSET_CONTENT_VIEWPORT_HEIGHT_FIELD])
  const label = nodeInstanceLabel(nodeData.nodeType, fields.label as string | undefined, def.label)
  const isExpanded = Boolean(nodeData.isDetailExpanded)
  const hasText = persistedText.trim().length > 0

  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPersistRef = useRef<string | null>(null)
  const copyFeedbackTimerRef = useRef<number | null>(null)
  const resizeSessionRef = useRef<{ startY: number; startHeight: number } | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()

  const [liveViewportHeight, setLiveViewportHeight] = useState<number | null>(null)
  const [copyFeedback, setCopyFeedback] = useState(false)
  const [nodeHovered, setNodeHovered] = useState(false)

  const contentViewportHeight = liveViewportHeight ?? persistedViewportHeight

  const {
    text,
    setText,
    undo,
    redo,
    canUndo,
    canRedo,
    onFocus: onHistoryFocus,
    onBlur: onHistoryBlur,
    flushHistoryGroup,
    resetHistory,
  } = useTextEditHistory(persistedText)

  const flushPersist = useCallback(() => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    if (pendingPersistRef.current === null) return
    const next = pendingPersistRef.current
    pendingPersistRef.current = null
    nodeData.onUpdateTextAsset?.(id, next)
  }, [id, nodeData])

  const schedulePersist = useCallback(
    (next: string) => {
      pendingPersistRef.current = next
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
      persistTimerRef.current = setTimeout(() => flushPersist(), AUTOSAVE_DEBOUNCE_MS)
    },
    [flushPersist],
  )

  const handleTextChange = useCallback(
    (next: string) => {
      setText(next)
      // Debounce ordinary keystrokes, but flush immediately on large jumps
      // (paste / replace / select-all+type). Input method must not decide
      // whether a Workspace text asset is created (VistoeraTASK-00585).
      const prevLen = (pendingPersistRef.current ?? text).length
      schedulePersist(next)
      if (Math.abs(next.length - prevLen) >= 24 || (prevLen === 0 && next.trim().length > 0 && next.length >= 24)) {
        flushPersist()
      }
    },
    [flushPersist, schedulePersist, setText, text],
  )

  const { inputProps, applyExternalValue } = useCanvasImeSafeTextHandlers({
    value: text,
    onValueChange: handleTextChange,
  })

  const commitViewportHeight = useCallback(
    (nextHeight: number) => {
      const clamped = clampTextAssetContentViewportHeight(nextHeight)
      setLiveViewportHeight(null)
      if (clamped === persistedViewportHeight) return
      nodeData.onUpdateTextAssetPresentation?.(id, {
        [TEXT_ASSET_CONTENT_VIEWPORT_HEIGHT_FIELD]: clamped,
      })
      window.requestAnimationFrame(() => updateNodeInternals(id))
    },
    [id, nodeData, persistedViewportHeight, updateNodeInternals],
  )

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, isExpanded, text, updateNodeInternals])

  useEffect(() => {
    if (!isExpanded) {
      flushHistoryGroup()
      flushPersist()
      return
    }
    const frame = window.requestAnimationFrame(() => textareaRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [flushHistoryGroup, flushPersist, isExpanded])

  useEffect(
    () => () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
      if (copyFeedbackTimerRef.current != null) window.clearTimeout(copyFeedbackTimerRef.current)
      flushPersist()
    },
    [flushPersist],
  )

  useEffect(() => {
    if (isExpanded) return
    resetHistory(persistedText)
  }, [isExpanded, persistedText, resetHistory])

  useEffect(() => {
    if (resizeSessionRef.current) return
    setLiveViewportHeight(null)
  }, [persistedViewportHeight])

  const handleBodyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!isExpanded) nodeData.onExpandFunctionCardDetail?.(id)
  }

  const applyHistoryText = useCallback(
    (next: string | null) => {
      if (next === null) return
      applyExternalValue(next)
      schedulePersist(next)
    },
    [applyExternalValue, schedulePersist],
  )

  const handleUndoClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    applyHistoryText(undo())
  }

  const handleRedoClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    applyHistoryText(redo())
  }

  const handleCopyTextClick = async (e: ReactMouseEvent) => {
    e.stopPropagation()
    const payload = text.trim() ? text : persistedText
    if (!payload.trim()) return
    try {
      await copyGenerateTextResultToClipboard(payload)
      setCopyFeedback(true)
      if (copyFeedbackTimerRef.current != null) window.clearTimeout(copyFeedbackTimerRef.current)
      copyFeedbackTimerRef.current = window.setTimeout(() => {
        setCopyFeedback(false)
        copyFeedbackTimerRef.current = null
      }, COPY_FEEDBACK_MS)
    } catch {
      message.error(t('mediaCard.copyFailed'))
    }
  }

  const handleDuplicateClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    flushPersist()
    nodeData.onDuplicateNode?.(id)
  }

  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    flushPersist()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const handleEditorKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    stopCanvasEditableKeyPropagation(e)
    const mod = e.metaKey || e.ctrlKey
    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault()
      applyHistoryText(undo())
      return
    }
    if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
      e.preventDefault()
      applyHistoryText(redo())
    }
  }

  const handleResizePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const target = e.currentTarget
    target.setPointerCapture(e.pointerId)
    resizeSessionRef.current = { startY: e.clientY, startHeight: contentViewportHeight }
    setLiveViewportHeight(contentViewportHeight)

    const handlePointerMove = (ev: PointerEvent) => {
      const session = resizeSessionRef.current
      if (!session) return
      const next = clampTextAssetContentViewportHeight(session.startHeight + (ev.clientY - session.startY))
      setLiveViewportHeight(next)
    }

    const handlePointerUp = (ev: PointerEvent) => {
      target.releasePointerCapture(ev.pointerId)
      target.removeEventListener('pointermove', handlePointerMove)
      target.removeEventListener('pointerup', handlePointerUp)
      target.removeEventListener('pointercancel', handlePointerUp)
      const session = resizeSessionRef.current
      resizeSessionRef.current = null
      if (!session) return
      const finalHeight = clampTextAssetContentViewportHeight(
        session.startHeight + (ev.clientY - session.startY),
      )
      commitViewportHeight(finalHeight)
    }

    target.addEventListener('pointermove', handlePointerMove)
    target.addEventListener('pointerup', handlePointerUp)
    target.addEventListener('pointercancel', handlePointerUp)
  }

  const headerActions = (
    <>
      <Tooltip title={t('common.copy')} mouseEnterDelay={0.3}>
        <button type="button" className="pcv-media-hover-btn" aria-label={t('common.copy')} onClick={handleDuplicateClick}>
          <CopyOutlined />
        </button>
      </Tooltip>
      <Tooltip title={t('common.delete')} mouseEnterDelay={0.3}>
        <button
          type="button"
          className="pcv-media-hover-btn pcv-media-hover-btn--danger"
          aria-label={t('common.delete')}
          onClick={handleDeleteClick}
        >
          <CloseOutlined />
        </button>
      </Tooltip>
    </>
  )

  const nodeWidth = TEXT_ASSET_CARD_WIDTH_PX
  const expandedCopyEnabled = Boolean(text.trim() || persistedText.trim())
  const showQuickActions =
    hasText &&
    Boolean(
      nodeData.onQuickCreateGenerateTextFromTextAsset ||
        nodeData.onQuickCreateGenerateImageFromTextAsset ||
        nodeData.onQuickCreateGenerateVideoFromTextAsset ||
        nodeData.onQuickCreateScriptFromTextAsset,
    )

  return (
    <div
      className={`pcv-node pcv-node--text-asset${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(isExpanded)}${isExpanded ? ' is-editing' : ''}${nodeHovered ? ' is-result-zone-active' : ''}`}
      style={{ width: nodeWidth }}
      data-testid="canvas-text-asset-node"
      data-node-id={id}
      data-expanded={isExpanded ? 'yes' : 'no'}
      data-content-viewport-height={isExpanded ? contentViewportHeight : undefined}
      onMouseEnter={() => setNodeHovered(true)}
      onMouseLeave={() => setNodeHovered(false)}
    >
      <div className="pcv-node-inner">
        <CanvasFunctionCardHeader
          variant="brief"
          icon={def.icon}
          color={def.color}
          title={label}
          actions={headerActions}
          className="pcv-text-asset-header"
          data-testid="canvas-text-asset-header"
        />

        {isExpanded ? (
          <div className="pcv-text-asset-body pcv-text-asset-body--expanded nodrag nowheel">
            <div
              className="pcv-text-asset-editor-scroll"
              style={{ height: contentViewportHeight }}
              data-testid="canvas-text-asset-editor-scroll"
            >
              <textarea
                ref={textareaRef}
                className="pcv-text-asset-editor pcv-canvas-result-plaintext nowheel nodrag"
                rows={8}
                placeholder={t('mediaCard.textInputPlaceholder')}
                style={{ height: '100%', minHeight: 0 }}
                {...inputProps}
                onKeyDown={(e) => {
                  inputProps.onKeyDown(e)
                  handleEditorKeyDown(e)
                }}
                onFocus={() => {
                  inputProps.onFocus()
                  onHistoryFocus()
                }}
                onBlur={() => {
                  inputProps.onBlur()
                  onHistoryBlur()
                }}
              />
            </div>
            <div
              className="pcv-text-asset-content-resize-handle nodrag nopan"
              role="separator"
              aria-orientation="horizontal"
              aria-label={t('mediaCard.resizeContentHeight')}
              data-testid="canvas-text-asset-content-resize-handle"
              onPointerDown={handleResizePointerDown}
            />
            <div className="pcv-text-asset-history-footer nodrag" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="pcv-text-asset-history-btn"
                aria-label={t('common.undo')}
                disabled={!canUndo}
                onClick={handleUndoClick}
              >
                <UndoOutlined />
                <span>{t('common.undo')}</span>
              </button>
              <button
                type="button"
                className="pcv-text-asset-history-btn"
                aria-label={t('common.redo')}
                disabled={!canRedo}
                onClick={handleRedoClick}
              >
                <RedoOutlined />
                <span>{t('common.redo')}</span>
              </button>
              <button
                type="button"
                className="pcv-text-asset-history-btn"
                aria-label={t('mediaCard.copyText')}
                disabled={!expandedCopyEnabled}
                data-testid="canvas-text-asset-copy-text-btn"
                onClick={handleCopyTextClick}
              >
                <SnippetsOutlined />
                <span>{copyFeedback ? t('mediaCard.copied') : t('mediaCard.copyText')}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="pcv-text-asset-body pcv-node-expand-trigger" onClick={handleBodyClick}>
            {hasText ? (
              <p className="pcv-text-asset-preview pcv-canvas-result-plaintext">{persistedText}</p>
            ) : (
              <p className="pcv-text-asset-preview pcv-canvas-result-plaintext pcv-text-asset-preview--empty">
                {t('mediaCard.textInputPlaceholder')}
              </p>
            )}
          </div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="text-out"
        className="pcv-handle pcv-handle--lg"
        data-testid="text-asset-out-handle"
      />

      {showQuickActions ? (
        <CanvasResultQuickActionRail
          sourceNodeType="text_asset"
          className="pcv-media-workflow-toolbar--text-asset"
          onMouseEnter={() => setNodeHovered(true)}
          onMouseLeave={() => setNodeHovered(false)}
          actionHandlers={[
            {
              targetNodeType: 'generate_text',
              tooltip: t('mediaCard.quickActionGenerateText'),
              onClick: (e) => {
                e.stopPropagation()
                nodeData.onQuickCreateGenerateTextFromTextAsset?.(id)
              },
            },
            {
              targetNodeType: 'generate_image',
              tooltip: t('mediaCard.quickActionGenerateImage'),
              onClick: (e) => {
                e.stopPropagation()
                nodeData.onQuickCreateGenerateImageFromTextAsset?.(id)
              },
            },
            {
              targetNodeType: 'generate_video',
              tooltip: t('mediaCard.quickActionGenerateVideo'),
              onClick: (e) => {
                e.stopPropagation()
                nodeData.onQuickCreateGenerateVideoFromTextAsset?.(id)
              },
            },
            {
              targetNodeType: 'script_generation',
              tooltip: t('mediaCard.quickActionGenerateScript'),
              onClick: (e) => {
                e.stopPropagation()
                nodeData.onQuickCreateScriptFromTextAsset?.(id)
              },
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default memo(TextAssetNodeImpl)
